import { useState, useRef } from 'react'
import useSWRMutation from 'swr/mutation'
import { reactMount } from '@/lib/reactMount'
import { Button } from '@/components/Button'
import { BrandLogo, BrandLogoIcon } from '@/components/Logos'
import { FormError } from '@/components/FormError'
import { fetcher } from '@/lib/fetcher'
import { formatDisplayDate } from '@/lib/date'

export const StockTransferReview = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  // CSS Helper classes
  const dlContainerClasses = 'font-extrabold space-y-3.5 text-[--base-text] border-b border-default-300 pb-8'
  const dlClasses = 'flex text-default-500 text-sm tracking-tight w-full'
  const dtClasses = 'w-1/3 m-w-[240px] pr-4'
  const ddClasses = 'flex-grow'

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitAction, setSubmitAction] = useState(null) // 'accept' | 'decline' | null
  const [result, setResult] = useState(null) // 'accepted' | 'declined' | null

  const { trigger: approve, error: approveError } = useSWRMutation(`/Api/Stock/Approve/${umbraco.StockTransfer.Id}`, fetcher)
  const { trigger: decline, error: declineError } = useSWRMutation(`/Api/Stock/Decline/${umbraco.StockTransfer.Id}`, fetcher)

  const apiErrorMessage = approveError?.responseMessage || declineError?.responseMessage || ''

  const handleAccept = async () => {
    setIsSubmitting(true)
    setSubmitAction('accept')
    try {
      await approve({ method: 'PUT' })
      setResult('accepted')
    } catch (e) {
      // SWR mutation error is exposed via approveError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDecline = async () => {
    setIsSubmitting(true)
    setSubmitAction('decline')
    try {
      await decline({ method: 'PUT' })
      setResult('declined')
    } catch (e) {
      // SWR mutation error is exposed via declineError
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <header data-theme='light' className='_site-header mx-auto flex max-w-7xl items-center justify-between py-4 px-4 md:px-6 lg:px-8'>
        <div className='flex text-white mr-2 gap-x-4 items-center'>
          <a href='/' className='-m-1.5 p-1.5'>
            <span className='sr-only'>UDC</span>
            <BrandLogo className='hidden custom-lg:block' />
            <BrandLogoIcon className='custom-lg:hidden' />
          </a>
          <div className='h-6 border-l border-default-300' />
          { umbraco.Logo && umbraco.Logo.Url && (
              <div className='flex h-[53px] pl-1'>
                <img className='h-full w-auto' src={umbraco.Logo.Url} alt={umbraco.BrandDisplayName} />
              </div>
            )}
        </div>
      </header>
      <section className='relative isolate pt-12 pb-16 px-4 md:px-6 lg:px-10 xl:px-24'>
        <header className='max-w-[680px] text-center mx-auto mb-8'>
          <span className='uppercase block text-[10px] font-extrabold text-default-400 tracking-[2.025px] mb-2.5'>Inter-dealership Vehicle Transfer Form</span>
          <h1 className='text-3xl font-extrabold tracking-tight text-primary-950 sm:text-4xl text-balance mb-2.5'>
            {umbraco.StockTransfer.Status !== 'Pending' ? (
              <>Sorry this transfer has already been processed.</>
            ) : result === 'accepted' ? (
              <>Thank you. The transfer of the vehicle has been accepted.</>
            ) : result === 'declined' ? (
              <>The vehicle transfer request has now been declined.</>
            ) : (
              <>Confirm Vehicle Transfer</>
            )}
          </h1>
          {umbraco.StockTransfer.Status === 'Pending' && (
            <div className='_simple-rte mt-5 text-base font-semibold tracking-tight text-[--base-text]'>
              {result === 'accepted' ? (
                <>
                  <p>A confirmation will now be sent to {umbraco.SourceDealer.DealerName}. UDC will now process this vehicle transfer in both bailment systems per your request.</p>
                  <p>If you have any questions, please contact your UDC Account Manager.</p>
                </>
              ) : result === 'declined' ? (
                <>
                  <p>A notification will now be sent to {umbraco.SourceDealer.DealerName}.</p>
                  <p>Your UDC Account Manager may be in touch regarding this request.</p>
                  <p>If you have any questions, please contact your UDC Account Manager.</p>
                </>
              ) : (
                <p>Below you will find the necessary information required in order to transfer a vehicle to your bailment facility from another dealership within the {umbraco.BrandDisplayName} Dealer Network.</p>
              )}
            </div>
          )}
        </header>
        {!result && umbraco.StockTransfer.Status === 'Pending' && (
        <div className='mx-auto max-w-[680px] space-y-6'>
          <div className='mx-auto space-y-8'>
            <div className='space-y-8 font-extrabold pt-2'>
              <div className={dlContainerClasses}>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Record ID</dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.Id}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Requestor </dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.Name}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Vehicle Stock Number</dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.VehicleStockNumber}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Make</dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.Make}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Model</dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.Model}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Colour</dt>
                  <dd className={ddClasses}>{umbraco.StockTransfer.Color}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>Effective Transfer Date</dt>
                  <dd className={ddClasses}>{formatDisplayDate(umbraco.StockTransfer.EffectiveStockTransferDate)}</dd>
                </dl>
              </div>
              <div className={dlContainerClasses}>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>From Dealership</dt>
                  <dd className={ddClasses}>{umbraco.SourceDealer.DealerName}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>From Dealership Phone</dt>
                  <dd className={ddClasses}>{umbraco.SourceDealer.DealerPhoneNumber}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>From Dealership Email</dt>
                  <dd className={ddClasses}>{umbraco.SourceDealer.StockTransferEmailAddress?.trim() || umbraco.SourceDealer.DealerPrimaryContactEmail}</dd>
                </dl>
              </div>
              <div className={dlContainerClasses}>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>To Dealership</dt>
                  <dd className={ddClasses}>{umbraco.DestinationDealer.DealerName}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>To Dealership Phone</dt>
                  <dd className={ddClasses}>{umbraco.DestinationDealer.DealerPhoneNumber}</dd>
                </dl>
                <dl className={dlClasses}>
                  <dt className={dtClasses}>To Dealership Email</dt>
                  <dd className={ddClasses}>{umbraco.DestinationDealer.StockTransferEmailAddress?.trim() || umbraco.DestinationDealer.DealerPrimaryContactEmail}</dd>
                </dl>
              </div>

              <div className='flex gap-x-3'>
                <Button onClick={handleAccept} disabled={isSubmitting} variant='primary' size='lg'>
                  {isSubmitting && submitAction === 'accept' ? 'Accepting...' : 'Accept'}
                </Button>
                <Button onClick={handleDecline} disabled={isSubmitting} variant='secondary' size='lg'>
                  {isSubmitting && submitAction === 'decline' ? 'Declining...' : 'Decline'}
                </Button>
              </div>
              <FormError error={apiErrorMessage} />
              <div className='text-sm font-semibold space-y-3 text-[--base-text]'>
                <h4 className='font-extrabold text-primary-950'>Disclaimer:</h4>
                <p>By accepting this transfer of the above vehicle, you acknowledge you have obligations in respect of this transfer pursuant to your bailment agreement.</p>
              </div>
            </div>
          </div>
        </div>
        )}
      </section>
    </>
  )
}

reactMount('StockTransferReview', StockTransferReview)
